import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  ExternalLink,
  Globe,
  Pencil,
  Trash2,
  Send,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  useContentList,
  useDeleteContent,
  useDispatchContent,
  type Content,
  type ContentStatus,
} from '@/hooks/useContent';
import { useSites, useSiteCategories, useSiteTags, type Site } from '@/hooks/useSites';
import { TaxonomyPicker } from '@/components/content/TaxonomyPicker';
import { useAlert, useConfirm } from '@/components/ConfirmDialog';
import { api, ApiError } from '@/lib/api-client';
import { formatDate, formatUsd } from '@/lib/utils';
import { cn } from '@/lib/utils';

const canDispatchContent = (c: Content) =>
  !!c.body_html || c.status === 'ready' || c.status === 'published';

export function ContentPage() {
  const { data, isPending } = useContentList();
  const items = data?.content ?? [];
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Drop selections that no longer exist (e.g. after a bulk delete).
  useEffect(() => {
    if (selected.size === 0) return;
    const ids = new Set(items.map((c) => c.id));
    let changed = false;
    const next = new Set<string>();
    for (const id of selected) {
      if (ids.has(id)) next.add(id);
      else changed = true;
    }
    if (changed) setSelected(next);
  }, [items, selected]);

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const clear = () => setSelected(new Set());
  const selectAll = () => setSelected(new Set(items.map((c) => c.id)));
  const allSelected = items.length > 0 && selected.size === items.length;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold">İçerikler</h1>
          <p className="text-muted-foreground text-sm">
            Üretilmiş taslaklar, yayına hazır makaleler, yayınlananlar.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <Link to="/content/bulk">Toplu Oluştur</Link>
          </Button>
          <Button asChild>
            <Link to="/content/new">
              <Plus className="w-4 h-4" /> Yeni İçerik
            </Link>
          </Button>
        </div>
      </div>

      {selected.size > 0 && (
        <BulkActionBar
          selectedIds={Array.from(selected)}
          items={items}
          allSelected={allSelected}
          onSelectAll={selectAll}
          onClear={clear}
        />
      )}

      {isPending ? (
        <div className="text-muted-foreground">Yükleniyor…</div>
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-2">
          {items.map((c) => (
            <ContentCard
              key={c.id}
              content={c}
              selected={selected.has(c.id)}
              onToggleSelect={() => toggleOne(c.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <Card>
      <CardContent className="py-12 text-center space-y-3">
        <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center">
          <FileText className="w-6 h-6 text-muted-foreground" />
        </div>
        <div>
          <p className="font-medium">Henüz içerik yok</p>
          <p className="text-sm text-muted-foreground">AI ile ilk makaleni üret.</p>
        </div>
        <Button asChild>
          <Link to="/content/new">
            <Plus className="w-4 h-4" /> İlk İçeriği Oluştur
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function ContentCard({
  content,
  selected,
  onToggleSelect,
}: {
  content: Content;
  selected: boolean;
  onToggleSelect: () => void;
}) {
  const navigate = useNavigate();
  const del = useDeleteContent();
  const dispatch = useDispatchContent(content.id);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const confirm = useConfirm();
  const alertDialog = useAlert();

  const goToDetail = () => navigate({ to: '/content/$id', params: { id: content.id } });
  const stop = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const canDispatch = canDispatchContent(content);
  const isBusy = dispatch.isPending || del.isPending;

  const onDelete = async (e: React.MouseEvent) => {
    stop(e);
    const ok = await confirm({
      title: 'İçeriği sil?',
      description: `"${content.title}" silinecek. Geri alınamaz.`,
      confirmText: 'Sil',
      danger: true,
    });
    if (!ok) return;
    await del.mutateAsync(content.id);
  };

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={goToDetail}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && goToDetail()}
      className={cn(
        'block transition-colors hover:bg-accent/40 rounded-lg cursor-pointer',
        selected && 'ring-1 ring-primary/40',
      )}
    >
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            {/* Only stopPropagation — NOT preventDefault — or the
                checkbox's native toggle is cancelled and React's onChange
                fires for the next click instead of the current one. */}
            <div onClick={(e) => e.stopPropagation()} className="pt-1">
              <input
                type="checkbox"
                checked={selected}
                onChange={onToggleSelect}
                aria-label="Seç"
                className="h-4 w-4 rounded border-input cursor-pointer"
              />
            </div>
            <StatusIcon status={content.status} />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-medium truncate">{content.title}</h3>
                <StatusBadge status={content.status} />
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                {content.focus_keyword && (
                  <span className="inline-flex items-center gap-1">
                    <span className="font-medium">kw:</span> {content.focus_keyword}
                  </span>
                )}
                {content.ai_model && <span>{content.ai_model}</span>}
                {content.ai_credits_consumed != null && (
                  <span className="font-mono">{formatUsd(content.ai_credits_consumed)}</span>
                )}
                {content.site_name && (
                  <span className="inline-flex items-center gap-1">
                    <Globe className="w-3 h-3" /> {content.site_name}
                  </span>
                )}
                <span>Güncellendi: {formatDate(content.updated_at)}</span>
              </div>
              {content.status === 'failed' && content.error_message && (
                <div className="mt-2 text-xs text-destructive bg-destructive/10 rounded px-2 py-1">
                  {content.error_message}
                </div>
              )}
              {content.remote_post_url && (
                <a
                  href={content.remote_post_url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  onClick={stop}
                >
                  Yayındaki URL <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            {/* Action buttons — stopPropagation so they don't trigger the card's navigate. */}
            <div className="flex items-center gap-1 shrink-0" onClick={stop}>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0"
                title="Düzenle"
                onClick={(e) => {
                  stop(e);
                  goToDetail();
                }}
                disabled={isBusy}
              >
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              {canDispatch && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0"
                  title="Siteye gönder"
                  onClick={(e) => {
                    stop(e);
                    setDispatchOpen(true);
                  }}
                  disabled={isBusy}
                >
                  {dispatch.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5" />
                  )}
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                title="Sil"
                onClick={onDelete}
                disabled={isBusy}
              >
                {del.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      {dispatchOpen && (
        <DispatchModal
          content={content}
          onClose={() => setDispatchOpen(false)}
          onDispatch={async (siteId, categoryIds, tagIds) => {
            try {
              await dispatch.mutateAsync({
                site_id: siteId,
                status: 'publish',
                category_ids: categoryIds.length > 0 ? categoryIds : undefined,
                tag_ids: tagIds.length > 0 ? tagIds : undefined,
              });
              setDispatchOpen(false);
            } catch (err) {
              await alertDialog({
                title: 'Gönderim başarısız',
                description: err instanceof Error ? err.message : 'Bilinmeyen hata',
                danger: true,
              });
            }
          }}
          isBusy={dispatch.isPending}
        />
      )}
    </div>
  );
}

/** Sticky bar shown when one or more rows are selected. Exposes the two
 *  bulk actions we support: delete and dispatch. Runs the underlying API
 *  calls in parallel with Promise.allSettled so a single failure doesn't
 *  abort the rest — the user sees a success/failure count at the end. */
function BulkActionBar({
  selectedIds,
  items,
  allSelected,
  onSelectAll,
  onClear,
}: {
  selectedIds: string[];
  items: Content[];
  allSelected: boolean;
  onSelectAll: () => void;
  onClear: () => void;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const alertDialog = useAlert();
  const [busy, setBusy] = useState<null | 'delete' | 'dispatch'>(null);
  const [progress, setProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
  const [dispatchOpen, setDispatchOpen] = useState(false);

  const selectedItems = useMemo(
    () => items.filter((c) => selectedIds.includes(c.id)),
    [items, selectedIds],
  );
  const dispatchable = useMemo(() => selectedItems.filter(canDispatchContent), [selectedItems]);
  const nonDispatchableCount = selectedItems.length - dispatchable.length;

  const runParallel = async <T,>(
    ids: string[],
    fn: (id: string) => Promise<T>,
  ): Promise<{ ok: number; failed: Array<{ id: string; err: string }> }> => {
    setProgress({ done: 0, total: ids.length });
    let done = 0;
    const results = await Promise.allSettled(
      ids.map((id) =>
        fn(id).finally(() => {
          done += 1;
          setProgress({ done, total: ids.length });
        }),
      ),
    );
    const failed: Array<{ id: string; err: string }> = [];
    let ok = 0;
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') ok += 1;
      else {
        const msg =
          r.reason instanceof ApiError
            ? r.reason.message
            : r.reason instanceof Error
              ? r.reason.message
              : 'Bilinmeyen hata';
        failed.push({ id: ids[i], err: msg });
      }
    });
    return { ok, failed };
  };

  const onBulkDelete = async () => {
    const ok = await confirm({
      title: `${selectedIds.length} içeriği sil?`,
      description: 'Seçili içerikler kalıcı olarak silinecek. Geri alınamaz.',
      confirmText: 'Sil',
      danger: true,
    });
    if (!ok) return;
    setBusy('delete');
    try {
      const res = await runParallel(selectedIds, (id) =>
        api.del<{ deleted: boolean }>(`/api/app/content/${id}`),
      );
      qc.invalidateQueries({ queryKey: ['content'] });
      onClear();
      if (res.failed.length > 0) {
        await alertDialog({
          title: 'Bazıları silinemedi',
          description: `${res.ok} silindi, ${res.failed.length} başarısız. İlk hata: ${res.failed[0].err}`,
          danger: true,
        });
      }
    } finally {
      setBusy(null);
    }
  };

  const onBulkDispatch = async (siteId: string, categoryIds: number[], tagIds: number[]) => {
    setBusy('dispatch');
    setDispatchOpen(false);
    try {
      const res = await runParallel(
        dispatchable.map((c) => c.id),
        (id) =>
          api.post(`/api/app/content/${id}/dispatch`, {
            site_id: siteId,
            status: 'publish',
            category_ids: categoryIds.length > 0 ? categoryIds : undefined,
            tag_ids: tagIds.length > 0 ? tagIds : undefined,
          }),
      );
      qc.invalidateQueries({ queryKey: ['content'] });
      onClear();
      if (res.failed.length > 0) {
        await alertDialog({
          title: 'Bazıları gönderilemedi',
          description: `${res.ok} gönderildi, ${res.failed.length} başarısız. İlk hata: ${res.failed[0].err}`,
          danger: true,
        });
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className="sticky top-2 z-20 rounded-lg border bg-background/95 backdrop-blur px-3 py-2 shadow-sm flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={allSelected ? onClear : onSelectAll}
            className="h-4 w-4 rounded border-input cursor-pointer"
          />
          <span className="font-medium">{selectedIds.length} seçili</span>
          {!allSelected && (
            <button type="button" onClick={onSelectAll} className="text-xs text-primary hover:underline">
              Tümünü seç
            </button>
          )}
        </label>

        {busy ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            {busy === 'delete' ? 'Siliniyor' : 'Gönderiliyor'} {progress.done}/{progress.total}…
          </div>
        ) : (
          <div className="flex items-center gap-2 ml-auto">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDispatchOpen(true)}
              disabled={dispatchable.length === 0}
              title={
                dispatchable.length === 0
                  ? 'Seçili içeriklerden hiçbiri gönderilmeye hazır değil'
                  : undefined
              }
            >
              <Send className="w-3.5 h-3.5" />
              Siteye Gönder{nonDispatchableCount > 0 && ` (${dispatchable.length})`}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onBulkDelete}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Sil
            </Button>
            <Button size="sm" variant="ghost" onClick={onClear}>
              <X className="w-3.5 h-3.5" />
              Temizle
            </Button>
          </div>
        )}

        {!busy && nonDispatchableCount > 0 && (
          <p className="w-full text-xs text-muted-foreground">
            {nonDispatchableCount} içerik henüz hazır değil — gönderimde atlanır.
          </p>
        )}
      </div>

      {dispatchOpen && (
        <BulkDispatchModal
          count={dispatchable.length}
          onClose={() => setDispatchOpen(false)}
          onDispatch={onBulkDispatch}
        />
      )}
    </>
  );
}

/** Bulk version of DispatchModal — same site + taxonomy picker but no
 *  per-item context since it applies to N contents. */
function BulkDispatchModal({
  count,
  onClose,
  onDispatch,
}: {
  count: number;
  onClose: () => void;
  onDispatch: (siteId: string, categoryIds: number[], tagIds: number[]) => Promise<void>;
}) {
  const { data } = useSites();
  const sites = data?.sites ?? [];
  const [selected, setSelected] = useState<string>('');
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [tagIds, setTagIds] = useState<number[]>([]);
  const categoriesQ = useSiteCategories(selected || null);
  const tagsQ = useSiteTags(selected || null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setCategoryIds([]);
    setTagIds([]);
  }, [selected]);

  const modal = (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-semibold">Toplu Gönder</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {count} içerik seçili siteye gönderilecek.
              </p>
            </div>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          {sites.length === 0 ? (
            <div className="text-sm text-muted-foreground border rounded-md p-3">
              Henüz site eklenmemiş.{' '}
              <Link to="/sites/new" className="text-primary hover:underline">
                Site ekle →
              </Link>
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[40vh] overflow-y-auto">
              {sites.map((s: Site) => (
                <label
                  key={s.id}
                  className={cn(
                    'flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer hover:bg-accent/50',
                    selected === s.id && 'border-primary bg-primary/5',
                  )}
                >
                  <input
                    type="radio"
                    name="bulk-dispatch-site"
                    checked={selected === s.id}
                    onChange={() => setSelected(s.id)}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{s.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {s.site_url.replace(/^https?:\/\//, '')}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}

          {selected && (
            <div className="space-y-3 border-t pt-3">
              <TaxonomyPicker
                label="Kategoriler"
                emptyHint="Seçmezsen WP varsayılan kategoriyi kullanır."
                items={categoriesQ.data?.categories.map((c) => ({ id: c.id, name: c.name, count: c.count })) ?? []}
                loading={categoriesQ.isPending}
                error={categoriesQ.error instanceof Error ? categoriesQ.error.message : null}
                selected={categoryIds}
                onToggle={(id) =>
                  setCategoryIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
                }
              />
              <TaxonomyPicker
                label="Etiketler"
                emptyHint="Opsiyonel."
                items={tagsQ.data?.tags.map((t) => ({ id: t.id, name: t.name, count: t.count })) ?? []}
                loading={tagsQ.isPending}
                error={tagsQ.error instanceof Error ? tagsQ.error.message : null}
                selected={tagIds}
                onToggle={(id) =>
                  setTagIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
                }
              />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              İptal
            </Button>
            <Button
              onClick={async () => {
                if (!selected) return;
                setSubmitting(true);
                try {
                  await onDispatch(selected, categoryIds, tagIds);
                } finally {
                  setSubmitting(false);
                }
              }}
              disabled={!selected || submitting}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {count} İçeriği Gönder
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Her içerik için ayrı bir WP postu oluşturulur. Daha önce aynı siteye gönderilmiş içerikler güncellenir.
          </p>
        </CardContent>
      </Card>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modal, document.body) : modal;
}

/** Site picker modal for dispatching. Lists all org sites; pre-selects
 *  the content's current site_id so the common "re-send to same site"
 *  case is one click.
 *
 *  Rendered in a portal so clicks inside the modal don't bubble up to
 *  the ContentCard wrapper (which has role="link"). */
function DispatchModal({
  content,
  onClose,
  onDispatch,
  isBusy,
}: {
  content: Content;
  onClose: () => void;
  onDispatch: (siteId: string, categoryIds: number[], tagIds: number[]) => Promise<void>;
  isBusy: boolean;
}) {
  const { data } = useSites();
  const [selected, setSelected] = useState<string>(content.site_id ?? '');
  const sites = data?.sites ?? [];
  // Taxonomy selection — fetched lazily once a site is picked. Reset
  // whenever the site changes because ids are WP-specific.
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const categoriesQ = useSiteCategories(selected || null);
  const tagsQ = useSiteTags(selected || null);
  useEffect(() => {
    setSelectedCategoryIds([]);
    setSelectedTagIds([]);
  }, [selected]);

  const isSameAsCurrent = selected === content.site_id;
  const actionLabel =
    content.status === 'published' && isSameAsCurrent
      ? 'Tekrar Gönder'
      : isSameAsCurrent
        ? 'Gönder'
        : 'Bu Siteye Gönder';

  const modal = (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={(e) => {
        // Close when backdrop is clicked; prevent any parent (e.g. the
        // ContentCard's role=link wrapper) from receiving the event.
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Card
        className="w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-semibold">Siteye Gönder</h3>
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{content.title}</p>
            </div>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          {sites.length === 0 ? (
            <div className="text-sm text-muted-foreground border rounded-md p-3">
              Henüz site eklenmemiş.{' '}
              <Link to="/sites/new" className="text-primary hover:underline">
                Site ekle →
              </Link>
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
              {sites.map((s: Site) => {
                const isCurrent = s.id === content.site_id;
                return (
                  <label
                    key={s.id}
                    className={cn(
                      'flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer hover:bg-accent/50',
                      selected === s.id && 'border-primary bg-primary/5',
                    )}
                  >
                    <input
                      type="radio"
                      name="dispatch-site"
                      checked={selected === s.id}
                      onChange={() => setSelected(s.id)}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-2">
                        {s.name}
                        {isCurrent && (
                          <span className="text-[10px] rounded bg-primary/10 text-primary px-1.5 py-0.5">
                            önceki
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {s.site_url.replace(/^https?:\/\//, '')}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}

          {selected && (
            <div className="space-y-3 border-t pt-3">
              <TaxonomyPicker
                label="Kategoriler"
                emptyHint="Seçmezsen WP varsayılan kategoriyi kullanır (Uncategorized)."
                items={categoriesQ.data?.categories.map((c) => ({ id: c.id, name: c.name, count: c.count })) ?? []}
                loading={categoriesQ.isPending}
                error={categoriesQ.error instanceof Error ? categoriesQ.error.message : null}
                selected={selectedCategoryIds}
                onToggle={(id) =>
                  setSelectedCategoryIds((prev) =>
                    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
                  )
                }
              />
              <TaxonomyPicker
                label="Etiketler"
                emptyHint="Opsiyonel."
                items={tagsQ.data?.tags.map((t) => ({ id: t.id, name: t.name, count: t.count })) ?? []}
                loading={tagsQ.isPending}
                error={tagsQ.error instanceof Error ? tagsQ.error.message : null}
                selected={selectedTagIds}
                onToggle={(id) =>
                  setSelectedTagIds((prev) =>
                    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
                  )
                }
              />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} disabled={isBusy}>
              İptal
            </Button>
            <Button
              onClick={() => selected && onDispatch(selected, selectedCategoryIds, selectedTagIds)}
              disabled={!selected || isBusy}
            >
              {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {actionLabel}
            </Button>
          </div>
          {content.site_id && selected && selected !== content.site_id && (
            <p className="text-xs text-muted-foreground">
              Not: Farklı site seçince yeni bir WP postu oluşturulur — eski siteye gönderilen mevcut post etkilenmez.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Portal keeps the modal out of the ContentCard subtree so its events
  // never bubble back to the card's navigate handler.
  return typeof document !== 'undefined' ? createPortal(modal, document.body) : modal;
}

function StatusIcon({ status }: { status: ContentStatus }) {
  const cls = 'w-4 h-4 mt-1 shrink-0';
  switch (status) {
    case 'published':
      return <CheckCircle2 className={cn(cls, 'text-emerald-600 dark:text-emerald-400')} />;
    case 'failed':
      return <XCircle className={cn(cls, 'text-destructive')} />;
    case 'generating':
    case 'dispatching':
      return <Loader2 className={cn(cls, 'text-primary animate-spin')} />;
    case 'ready':
      return <FileText className={cn(cls, 'text-primary')} />;
    default:
      return <Clock className={cn(cls, 'text-muted-foreground')} />;
  }
}

function StatusBadge({ status }: { status: ContentStatus }) {
  const map: Record<ContentStatus, { label: string; cls: string }> = {
    draft: { label: 'Taslak', cls: 'bg-muted text-muted-foreground' },
    generating: { label: 'Üretiliyor…', cls: 'bg-primary/10 text-primary' },
    ready: { label: 'Hazır', cls: 'bg-primary/10 text-primary' },
    scheduled: { label: 'Planlandı', cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-400' },
    dispatching: { label: 'Gönderiliyor…', cls: 'bg-primary/10 text-primary' },
    published: {
      label: 'Yayında',
      cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
    },
    failed: { label: 'Hata', cls: 'bg-destructive/10 text-destructive' },
  };
  const m = map[status];
  return <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', m.cls)}>{m.label}</span>;
}
